import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --gold: #c9a84c; --gold-light: #e8c97a; --dark: #0e0e0e; --dark2: #161616;
  --dark3: #1e1e1e; --dark4: #252525; --text: #e0d8cc; --text-muted: #888;
  --accent: #c9a84c; --phone-w: 270px; --phone-h: 530px;
}
.ca-page {
  height: 100vh; width: 100vw;
  background: radial-gradient(ellipse 80% 60% at 50% 30%, #1a1506 0%, #0a0a0a 60%, #000 100%);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  font-family: 'DM Sans', sans-serif; overflow: hidden; position: relative;
}
.bg-particles { position: fixed; inset: 0; pointer-events: none; z-index: 0; }
.particle {
  position: absolute; width: 2px; height: 2px; background: var(--gold);
  border-radius: 50%; opacity: 0;
  animation: float-particle var(--dur, 6s) var(--delay, 0s) infinite ease-in-out;
}
@keyframes float-particle {
  0%, 100% { opacity: 0; transform: translateY(0) scale(0); }
  20% { opacity: 0.6; transform: translateY(-20px) scale(1); }
  80% { opacity: 0.3; transform: translateY(-80px) scale(0.5); }
}
.scene {
  position: relative; z-index: 2; display: flex; flex-direction: column; align-items: center; gap: 14px;
  transform-origin: center center;
}
@media (max-width: 360px), (max-height: 620px) { .scene { transform: scale(0.75); } }
@media (min-width: 361px) and (max-width: 480px) and (min-height: 621px) { .scene { transform: scale(0.88); } }
.headline {
  font-family: 'Cinzel', serif; font-size: 13px; letter-spacing: 0.3em;
  color: var(--gold); text-transform: uppercase; opacity: 0; transform: translateY(-10px);
  animation: fade-up 0.6s 0.3s forwards ease-out;
}
.phone {
  width: var(--phone-w); height: var(--phone-h); background: #111;
  border-radius: 40px; border: 2px solid #2a2a2a;
  box-shadow: 0 0 0 1px #3a3a2a, 0 30px 80px rgba(0,0,0,0.8), inset 0 0 30px rgba(0,0,0,0.5), 0 0 60px rgba(201,168,76,0.08);
  position: relative; overflow: hidden;
  animation: phone-appear 0.8s 0.5s both cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes phone-appear {
  from { opacity: 0; transform: scale(0.85) translateY(30px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.phone::before {
  content: ''; position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
  width: 90px; height: 22px; background: #0a0a0a; border-radius: 20px; z-index: 100;
}
.screen { width: 100%; height: 100%; background: #111; border-radius: 38px; overflow: hidden; position: relative; }
.card-scroll { position: absolute; inset: 0; overflow: hidden; }
.card-inner { width: 100%; display: flex; flex-direction: column; }
.banner {
  width: 100%; height: 90px; position: relative; overflow: hidden;
  opacity: 0; animation: banner-reveal 0.7s 1.3s both ease-out; flex-shrink: 0;
}
@keyframes banner-reveal {
  from { opacity: 0; transform: scaleX(0.9); clip-path: inset(0 50% 0 50%); }
  to   { opacity: 1; transform: scaleX(1);   clip-path: inset(0 0% 0 0%); }
}
.banner-img {
  width: 100%; height: 100%;
  background: linear-gradient(135deg, #1a0e00 0%, #2d1800 30%, #1a1200 60%, #0d0d0d 100%);
  position: relative;
}
.banner-img::before {
  content: ''; position: absolute; inset: 0;
  background-image: linear-gradient(rgba(201,168,76,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.15) 1px, transparent 1px);
  background-size: 20px 20px; mask-image: radial-gradient(ellipse at 70% 40%, black 20%, transparent 70%);
}
.banner-img::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(17,17,17,0.9) 100%);
}
.banner-orb {
  position: absolute; right: 20%; top: 20%; width: 60px; height: 60px;
  background: radial-gradient(circle, rgba(201,168,76,0.6) 0%, transparent 70%);
  border-radius: 50%; animation: orb-pulse 2s 2s infinite ease-in-out; opacity: 0; animation-fill-mode: both;
}
@keyframes orb-pulse {
  0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.3); }
}
.profile-area {
  display: flex; flex-direction: column; align-items: center;
  margin-top: -20px; padding: 0 16px 0; position: relative; z-index: 2;
  opacity: 0; animation: profile-pop 0.6s 2.0s both cubic-bezier(0.34, 1.56, 0.64, 1); flex-shrink: 0;
}
@keyframes profile-pop {
  from { opacity: 0; transform: scale(0.5) translateY(20px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}
.avatar-ring {
  width: 58px; height: 58px; border-radius: 50%; padding: 3px;
  background: conic-gradient(var(--gold), var(--gold-light), var(--gold), #6b4e00, var(--gold));
  box-shadow: 0 0 20px rgba(201,168,76,0.4); animation: ring-spin 8s 2.6s linear infinite; flex-shrink: 0;
}
@keyframes ring-spin { from { filter: hue-rotate(0deg); } to { filter: hue-rotate(360deg); } }
.avatar-inner {
  width: 100%; height: 100%; border-radius: 50%;
  background: linear-gradient(135deg, #3a2a10, #5c3d00, #2a1800);
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; overflow: hidden; border: 2px solid #0e0e0e;
}
.name-row { margin-top: 4px; text-align: center; }
.name-first { font-family: 'DM Sans', sans-serif; font-weight: 300; font-size: 16px; color: #e0d8cc; letter-spacing: 0.05em; }
.name-last { font-family: 'Cinzel', serif; font-size: 16px; font-weight: 600; color: var(--gold); letter-spacing: 0.08em; }
.designation-row {
  display: flex; gap: 4px; margin-top: 4px; justify-content: center; flex-wrap: wrap;
  padding: 0 10px; opacity: 0; animation: tags-slide 0.5s 2.7s both ease-out; flex-shrink: 0;
}
@keyframes tags-slide { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
.tag { padding: 4px 10px; border: 1px solid rgba(201,168,76,0.4); border-radius: 20px; font-size: 9px; color: var(--gold-light); letter-spacing: 0.08em; background: rgba(201,168,76,0.06); white-space: nowrap; }
.save-btn {
  margin: 5px 10px 0; padding: 7px;
  background: linear-gradient(135deg, var(--gold) 0%, #a07030 50%, var(--gold-light) 100%);
  border-radius: 8px; text-align: center; font-size: 10px; font-weight: 500;
  letter-spacing: 0.15em; color: #0e0e0e; text-transform: uppercase;
  opacity: 0; animation: btn-appear 0.5s 3.1s both ease-out; position: relative; overflow: hidden; flex-shrink: 0;
}
.save-btn::after {
  content: ''; position: absolute; top: 0; left: -100%; width: 60%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: btn-shine 2s 3.6s infinite;
}
@keyframes btn-appear { from { opacity: 0; transform: scaleX(0.8); } to { opacity: 1; transform: scaleX(1); } }
@keyframes btn-shine { 0% { left: -100%; } 100% { left: 200%; } }
.section { margin: 5px 10px 0; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; padding: 7px 10px; flex-shrink: 0; }
.section-label { font-size: 8px; letter-spacing: 0.2em; color: var(--gold); text-transform: uppercase; margin-bottom: 6px; font-weight: 500; }
.about-section { opacity: 0; animation: section-rise 0.6s 3.5s both ease-out; }
.about-text { font-size: 8.5px; line-height: 1.6; color: rgba(224,216,204,0.75); }
.contact-section { opacity: 0; animation: section-rise 0.6s 4.2s both ease-out; }
@keyframes section-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
.contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.contact-item { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; padding: 8px 10px; display: flex; align-items: flex-start; gap: 7px; }
.contact-icon { font-size: 12px; color: var(--gold); margin-top: 1px; flex-shrink: 0; }
.contact-text { font-size: 7.5px; color: rgba(224,216,204,0.7); line-height: 1.4; word-break: break-all; }
.connect-section { opacity: 0; animation: section-rise 0.6s 5.0s both ease-out; }
.connect-icons { display: flex; justify-content: center; gap: 16px; padding: 4px 0; }
.social-btn { width: 32px; height: 32px; border-radius: 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); display: flex; align-items: center; justify-content: center; font-size: 14px; }
.bottom-bar {
  margin: 5px 0 0; border-top: 1px solid rgba(255,255,255,0.07);
  background: rgba(10,10,10,0.95); padding: 6px 8px; display: flex; gap: 5px;
  flex-shrink: 0; opacity: 0; animation: bottom-slide 0.6s 5.7s both cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes bottom-slide { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
.bottom-btn { flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 7px 4px; text-align: center; }
.bottom-btn-icon { font-size: 12px; color: var(--gold); display: block; margin-bottom: 2px; }
.bottom-btn-label { font-size: 7px; color: rgba(224,216,204,0.6); letter-spacing: 0.05em; }
.bottom-btn-sub { font-size: 6px; color: rgba(201,168,76,0.5); letter-spacing: 0.05em; margin-top: 1px; }
.wallet-btn-bar { flex: 2; background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 7px 8px; display: flex; align-items: center; justify-content: center; gap: 6px; }
.wallet-text { font-size: 7.5px; color: rgba(224,216,204,0.8); letter-spacing: 0.05em; }
.step-dots { display: flex; gap: 8px; opacity: 0; animation: fade-up 0.5s 1.0s both ease-out; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(201,168,76,0.2); transition: background 0.4s, transform 0.4s; }
.dot.active { background: var(--gold); transform: scale(1.4); box-shadow: 0 0 8px var(--gold); }
@keyframes fade-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.scanline {
  position: absolute; width: 100%; height: 2px;
  background: linear-gradient(90deg, transparent, rgba(201,168,76,0.3), transparent);
  top: 0; left: 0; z-index: 50; opacity: 0; animation: scan 4s 1.2s ease-in-out;
}
@keyframes scan { 0% { top: 0; opacity: 0.8; } 100% { top: 100%; opacity: 0; } }
.ca-skip {
  font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 0.2em; color: var(--gold);
  background: transparent; border: 1px solid rgba(201,168,76,0.3); padding: 8px 20px;
  border-radius: 20px; cursor: pointer; transition: all 0.3s; text-transform: uppercase;
  opacity: 0; animation: fade-up 0.5s 6.3s both ease-out;
}
.ca-skip:hover { background: rgba(201,168,76,0.1); border-color: var(--gold); box-shadow: 0 0 20px rgba(201,168,76,0.2); }
`;

const DOT_TIMELINE = [
    { idx: 0, time: 500 }, { idx: 1, time: 1300 }, { idx: 2, time: 2000 },
    { idx: 3, time: 3500 }, { idx: 4, time: 4200 }, { idx: 5, time: 5000 }, { idx: 6, time: 5700 },
];

export default function CardAnimationPage() {
    const navigate = useNavigate();
    const particlesRef = useRef(null);
    const dotsRef = useRef(null);
    const timers = useRef([]);

    useEffect(() => {
        // Generate particles
        if (particlesRef.current) {
            for (let i = 0; i < 40; i++) {
                const p = document.createElement("div");
                p.className = "particle";
                p.style.left = Math.random() * 100 + "vw";
                p.style.bottom = Math.random() * 40 + "vh";
                p.style.setProperty("--dur", (4 + Math.random() * 6) + "s");
                p.style.setProperty("--delay", (Math.random() * 8) + "s");
                particlesRef.current.appendChild(p);
            }
        }

        // Dot timeline
        const dots = dotsRef.current?.querySelectorAll(".dot");
        if (dots) {
            DOT_TIMELINE.forEach(({ idx, time }) => {
                const t = setTimeout(() => {
                    dots.forEach(d => d.classList.remove("active"));
                    if (dots[idx]) dots[idx].classList.add("active");
                }, time);
                timers.current.push(t);
            });
        }

        // Auto-navigate to wallet animation after card animation completes
        const nav = setTimeout(() => navigate("/animation/wallet"), 7800);
        timers.current.push(nav);

        return () => timers.current.forEach(clearTimeout);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="ca-page">
            <style dangerouslySetInnerHTML={{ __html: CSS }} />
            <div className="bg-particles" ref={particlesRef} />

            <div className="scene">
                <div className="headline">Digital Business Card · Preview</div>

                <div style={{ position: "relative" }}>
                    <div className="phone">
                        <div className="screen">
                            <div className="scanline" />
                            <div className="card-scroll">
                                <div className="card-inner">
                                    <div className="banner">
                                        <div className="banner-img"><div className="banner-orb" /></div>
                                    </div>
                                    <div className="profile-area">
                                        <div className="avatar-ring">
                                            <div className="avatar-inner">🧑‍💻</div>
                                        </div>
                                        <div className="name-row">
                                            <span className="name-first">Your </span>
                                            <span className="name-last">Card</span>
                                        </div>
                                    </div>
                                    <div className="designation-row">
                                        <div className="tag">Your Title</div>
                                        <div className="tag">Your Company</div>
                                    </div>
                                    <div className="save-btn">⬇ Save Contact</div>
                                    <div className="section about-section">
                                        <div className="section-label">About</div>
                                        <div className="about-text">Share your story, skills, and expertise. Your digital card carries everything people need to know about you — in one elegant link.</div>
                                    </div>
                                    <div className="section contact-section">
                                        <div className="section-label">Contact</div>
                                        <div className="contact-grid">
                                            <div className="contact-item"><div className="contact-icon">📞</div><div className="contact-text">Your Phone</div></div>
                                            <div className="contact-item"><div className="contact-icon">✉️</div><div className="contact-text">Your Email</div></div>
                                            <div className="contact-item"><div className="contact-icon">🌐</div><div className="contact-text">myprofilecorner.com/you</div></div>
                                            <div className="contact-item"><div className="contact-icon">📍</div><div className="contact-text">Your Location</div></div>
                                        </div>
                                    </div>
                                    <div className="section connect-section">
                                        <div className="section-label">Connect</div>
                                        <div className="connect-icons">
                                            <div className="social-btn">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><defs><linearGradient id="ig" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#f09433"/><stop offset="50%" stopColor="#dc2743"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs><rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig)" strokeWidth="2" fill="none"/><circle cx="12" cy="12" r="4.5" stroke="url(#ig)" strokeWidth="2" fill="none"/><circle cx="17.5" cy="6.5" r="1.2" fill="url(#ig)"/></svg>
                                            </div>
                                            <div className="social-btn"><svg width="15" height="15" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="3" fill="#0A66C2"/><path d="M7 10h2v7H7v-7zm1-3a1.2 1.2 0 110 2.4A1.2 1.2 0 018 7zm4 3h2v1h.03C14.4 10.4 15.2 10 16 10c2.2 0 2.5 1.4 2.5 3.2V17h-2v-3.4c0-.8-.01-1.9-1.15-1.9-1.16 0-1.34.9-1.34 1.83V17H12v-7z" fill="white"/></svg></div>
                                            <div className="social-btn"><svg width="15" height="15" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#25D366"/><path d="M17.5 14.4c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.1c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4C8 8 7.3 8.7 7.3 10.1s1 2.8 1.1 3c.1.2 2 3 4.8 4.2.7.3 1.2.5 1.6.6.7.2 1.3.2 1.7.1.5-.1 1.7-.7 1.9-1.4.2-.6.2-1.2.1-1.3-.1-.1-.3-.2-.5-.3z" fill="white"/></svg></div>
                                            <div className="social-btn"><svg width="15" height="15" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#1877F2"/><path d="M15.5 8H13.5C13.2 8 13 8.2 13 8.5V10H15.5L15.2 12H13V19H11V12H9V10H11V8.5C11 7.1 12.1 6 13.5 6H15.5V8Z" fill="white"/></svg></div>
                                            <div className="social-btn"><svg width="15" height="15" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#24292e"/><path fillRule="evenodd" clipRule="evenodd" d="M12 6a6 6 0 00-1.897 11.693c.3.055.41-.13.41-.288v-1.13c-1.67.363-2.02-.713-2.02-.713-.273-.693-.667-.878-.667-.878-.545-.373.041-.365.041-.365.603.042.92.619.92.619.536.918 1.407.653 1.75.5.054-.388.21-.653.382-.803-1.333-.152-2.733-.667-2.733-2.965 0-.655.234-1.19.618-1.61-.062-.152-.268-.762.059-1.589 0 0 .504-.161 1.65.616A5.74 5.74 0 0112 9.28c.51.002 1.022.069 1.502.202 1.145-.777 1.648-.616 1.648-.616.328.827.122 1.437.06 1.589.385.42.617.955.617 1.61 0 2.305-1.403 2.812-2.74 2.96.216.186.408.552.408 1.112v1.648c0 .16.108.347.413.288A6.001 6.001 0 0012 6z" fill="white"/></svg></div>
                                        </div>
                                    </div>
                                    <div className="bottom-bar">
                                        <div className="bottom-btn"><span className="bottom-btn-icon">✏️</span><div className="bottom-btn-label">Edit Card</div><div className="bottom-btn-sub">Manage Info</div></div>
                                        <div className="bottom-btn"><span className="bottom-btn-icon">👁</span><div className="bottom-btn-label">View Profile</div><div className="bottom-btn-sub">Public page</div></div>
                                        <div className="wallet-btn-bar"><span>🟢</span><div className="wallet-text">Add to Google Wallet</div></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="step-dots" ref={dotsRef}>
                    {[0,1,2,3,4,5,6].map(i => <div key={i} className="dot" />)}
                </div>

                <button className="ca-skip" onClick={() => navigate("/animation/wallet")}>
                    Skip →
                </button>
            </div>
        </div>
    );
}
